import { useTranslation } from 'react-i18next';

import {
  ResultNoteRow,
  ResultRow,
  ResultSection,
  SolidBadge,
} from './ResultRow';
import { timingVerdict } from '../../resultCopy';

import type { DiagnosisRecord } from '../../record';
import type { SongSegmentScore } from '../../songScoring';

/**
 * 진단 3 — 노래 따라부르기(덱 p10): 음정 정확도 · 구간 분석 · 박자.
 *
 * 이 섹션의 값은 전부 `songScore`(레퍼런스 대비 채점)에서 온다. 그 진단을 건너뛴 회차는
 * **섹션이 "안 했다"고 말한다** — 0%로 그리면 부르지 않은 사람이 0점을 받은 것으로 읽힌다.
 * 축 카드의 "음정 정확도"와 이름이 겹치지 않게 여기서는 "노래 음정 정확도"다: 두 값은
 * 근거가 다르다(축은 지속음의 가장 가까운 반음 대비, 여기는 정답 음 대비).
 */

const segmentKey = (segment: SongSegmentScore, side: 'low' | 'high'): string =>
  segment.tendency
    ? `web.result.song.segment.${side}.${segment.tendency}`
    : 'web.result.song.segment.pending';

const TIMING_KEY = {
  steady: 'web.result.song.timingSteady',
  late: 'web.result.song.timingLate',
  early: 'web.result.song.timingEarly',
} as const;

export const SongSection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const song = record.songScore;

  if (!song) {
    return (
      <ResultSection title={t('web.result.song.section')}>
        <p className="text-sm text-muted-foreground">
          {t('web.result.song.skipped')}
        </p>
      </ResultSection>
    );
  }

  const timing = timingVerdict(song);
  return (
    <ResultSection title={t('web.result.song.section')}>
      <ResultRow
        label={t('web.result.song.accuracy')}
        // 축 카드(프록시)와 **다른 항목**이다: 여기 값만 정답 음 대비라 견고 배지를 단다.
        badge={<SolidBadge />}
        value={t('web.result.song.percent', {
          value: Math.round(song.accuracy),
        })}
        note={t('web.result.song.hits', {
          total: song.totalNotes,
          // 맞춘 음 수는 **부른 음** 기준 비율이다 — 안 부른 음까지 맞춘 것으로 세지 않는다.
          hit: Math.round(song.hitRatio * song.sungNotes),
        })}
      />
      <ResultNoteRow
        label={t('web.result.song.segments')}
        note={`${t(segmentKey(song.low, 'low'))} · ${t(
          segmentKey(song.high, 'high'),
        )}`}
      />
      <ResultNoteRow
        label={t('web.result.song.timing')}
        note={t(timing ? TIMING_KEY[timing] : 'web.result.song.timingPending')}
      />
    </ResultSection>
  );
};
